/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ContentType } from '../ContentType';
import { Messages } from '../../../../gen-src/Localize';
import { AppUrls } from '../../../app/AppUrls';
import { ConnectedOwnerFilter } from '../../components/filters/quickFilter/OwnerFilter';
import { CommonColumns, IColumnFactoryParameters } from '../../components/listView/ListViewGridColumnGenerator';
import { LocalizedCountCell } from '../../components/listView/dataGridCells/PlainTextAndNumberCells';
import { DataSource, SearchResultItem } from '../SearchResultTypes';
import { IContentDefinition } from './IContentDefinition';
import { ConnectedModifiedTimeFilter } from '../../components/filters/quickFilter/ModifiedTimeFilter';
import { EncryptionTooltip } from '../../components/tooltips/EncryptionTooltip';
import { LastRefreshedCell } from '../../components/listView/dataGridCells/LastRefreshedCell';
import { DataGrid } from '@tableau/widgets-datagrid';
import { SessionManagementService } from '../../services/SessionManagementService';
import { ExtractEncryptionMode } from '@tableau/microview-communication-service/lib/src/channelDefinitions/SessionManagementChannelDefinitions';
import {
  DatabaseConnectionDetail,
  FileConnectionDetail,
  GetDataSourcesResult,
  IDataSource,
  TableauServerConnectionDetail,
} from '../httpClient/MetaDataResults';
import GetMetaDataRequests from '../../services/httpClient/GetMetaDataRequests';
import { SearchResponseHitsContent } from '../../../generated/content-exploration';
import { UserMapper } from '../../../common/utils/UserMapper';
import { DataConnection } from '@tableau/search-microview-communication-channels';
import { MapperHelper } from '../../../common/utils/MapperHelper';
import { SortBy } from '../SortSelection';
import { defaultHeaderFormatter } from '../../components/listView/DefaultHeaderFormatter';
import { ILocationInfo, LocationTypes } from '../ILocationInfo';
import { DataSourceNameCell } from '../../components/listView/dataGridCells/DataSourceNameCell';
import { ConnectedTagFilter } from '../../components/filters/quickFilter/TagFilter';

export const dataSourceContentDefinition: IContentDefinition<DataSource, GetDataSourcesResult> = {
  getSectionTitle: () => Messages.DataSourceSectionTitle(),
  getUrl: (urls) => urls.searchDatasourcesUrl(),
  getPath: (urls) => urls.searchDatasourcesPath(),
  defaultDisplayMode: 'list',
  maxItemCountInMixedContentTypeResultPage: 8,
  contentSpaceUrl: (urls: AppUrls, contentId: string) => urls.datasourceUrl(contentId),
  quickFilters: [ConnectedOwnerFilter, ConnectedModifiedTimeFilter, ConnectedTagFilter],
  listViewColumns: [
    (parameters: IColumnFactoryParameters): DataGrid.Column => {
      return {
        name: 'name',
        dataKey: 'name',
        sortable: false,
        headerText: Messages.column_name(),
        direction: 'asc',
        preferredWidth: 100,
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: DataSourceNameCell(parameters),
      };
    },
    CommonColumns.owner,
    CommonColumns.location,
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'datasourceinworkbookscount',
        dataKey: 'datasourceinworkbookscount',
        sortable: false,
        direction: 'desc',
        headerText: Messages.column_numWorkbooks(),
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: LocalizedCountCell((data: DataSource) => data.connectedWorkbooksCount),
      };
    },
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'hits3months',
        dataKey: 'hits3months',
        sortable: false,
        direction: 'desc',
        headerText: Messages.column_hitsLastThreeMonthsTotal(),
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: LocalizedCountCell((data: DataSource) => data.usageInfo && data.usageInfo.hitsLastThreeMonthsTotal),
        maxColumnWidth: 10,
      };
    },
    (parameters: IColumnFactoryParameters) => {
      const showEncryptionTooltip: boolean =
        SessionManagementService.instance.isExtractEncryptionEnabledOmniSwitch() &&
        SessionManagementService.instance.getExtractEncryptionModeForSite() !== ExtractEncryptionMode.DISABLED;
      return {
        name: 'extractstatus',
        dataKey: 'lastRefreshedAt',
        sortable: false,
        headerText: Messages.extractStatusLabelTitle(),
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: LastRefreshedCell(
          parameters.listViewParams.urls,
          SessionManagementService.instance.isExtractEncryptionEnabledOmniSwitch(),
          SessionManagementService.instance.getExtractEncryptionModeForSite()
        ),
        growToFit: true,
        tooltip: showEncryptionTooltip ? EncryptionTooltip : null,
      };
    },
    CommonColumns.description,
  ],
  getMetaDataRequests: GetMetaDataRequests.GetDataSourcesRequest,
  mapCRASSData: (crassData: SearchResponseHitsContent[]) => {
    return crassData.map((crassDataItem: SearchResponseHitsContent) => {
      return {
        crassData: crassDataItem,
        contentType: ContentType.unifieddatasource,
        id: crassDataItem.id.toString() || '',
        name: crassDataItem.title,
        owner: UserMapper.mapOne({
          displayName: crassDataItem.ownerName || '',
          username: crassDataItem.ownerAlias || '',
          domainName: crassDataItem.ownerDomain,
          id: crassDataItem.ownerId?.toString() || '',
        }),
        description: crassDataItem.description,
        usageInfo: { hitsLastThreeMonthsTotal: crassDataItem.hitsMediumSpanTotal || 0 },
        datasourceId: crassDataItem.datasourceId?.toString() || '',
      };
    });
  },
  mapMetadata: (searchResultItems, metadata) => {
    const metadataMap: { [id: string]: IDataSource } = {};
    const metaDataSource: { [id: string]: { location: ILocationInfo } } = {};
    const favoriteMetaData: { [id: string]: boolean } = {};
    if (metadata.total > 0 && metadata.items.datasources) {
      metadata.items.datasources.forEach((item) => {
        metadataMap[item.id] = item;
      });
      metadata.items.projects.forEach((item) => {
        item.contents.DATASOURCE.forEach((data: number) => {
          metaDataSource[data.toString()] = {
            location: { name: item.name, id: item.id.toString(), type: LocationTypes.Project },
          };
        });
      });
      metadata.items.favorites.forEach((id) => {
        favoriteMetaData[id] = true;
      });
    }
    return searchResultItems.map((searchResultItem) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const crassDataItem = searchResultItem.crassData!;

      const metadataItem = metadataMap[crassDataItem.id];

      let lastRefreshTime: Date | undefined;

      const connectionDetails: DataConnection = {
        type: '',
        name: '',
        serverName: '',
        dataConnectionsCount: 0,
      };

      if (metadataItem) {
        connectionDetails.type = metadataItem.connectionDetails.type;
        const psExtracts = metadataItem.publishedDatasourceDetails ? metadataItem.publishedDatasourceDetails.hasExtracts : false;
        const esExtracts = metadataItem.hasExtracts;
        const allExtracts = psExtracts && esExtracts;

        if (allExtracts) {
          if (
            MapperHelper.toDate(metadataItem.publishedDatasourceDetails!.lastRefreshedAt)!.valueOf() >
            MapperHelper.toDate(metadataItem.lastRefreshedAt!)!.valueOf()
          ) {
            lastRefreshTime = MapperHelper.toDate(metadataItem.lastRefreshedAt!);
          } else {
            lastRefreshTime = MapperHelper.toDate(metadataItem.publishedDatasourceDetails!.lastRefreshedAt);
          }
        } else {
          lastRefreshTime = psExtracts
            ? MapperHelper.toDate(metadataItem.publishedDatasourceDetails!.lastRefreshedAt)
            : MapperHelper.toDate(metadataItem.lastRefreshedAt!);
        }

        switch (metadataItem.connectionDetails.type) {
          case 'file':
            connectionDetails.fileName = (metadataItem.connectionDetails as FileConnectionDetail).fileName;
            connectionDetails.hasEmbeddedPassword = (metadataItem.connectionDetails as FileConnectionDetail).hasEmbeddedPassword;
            break;
          case 'database':
            connectionDetails.hasEmbeddedPassword = (metadataItem.connectionDetails as DatabaseConnectionDetail).hasEmbeddedPassword;
            connectionDetails.serverName = (metadataItem.connectionDetails as DatabaseConnectionDetail).serverName;
            break;
          case 'tableauserver':
            connectionDetails.hasEmbeddedPassword = (metadataItem.connectionDetails as TableauServerConnectionDetail).hasEmbeddedPassword;
            break;
        }
      }

      return {
        ...searchResultItem,
        connectedWorkbooksCount: metadataItem ? metadataItem.connectedWorkbooksCount : 0,
        connectionDetails: connectionDetails,
        certificationDetails: metadataItem ? metadataItem.certificationDetails : undefined,
        dataConnectionsCount: metadataItem ? metadataItem.dataConnectionsCount : 0,
        dataFreshness: lastRefreshTime,
        hasExtracts: metadataItem ? metadataItem.hasExtracts : false,
        contentDetail: {
          dataQualityIndicators: metadataItem ? metadataItem.dataQualityIndicators : [],
        },
        publishedDatasourceDetails: metadataItem ? metadataItem.publishedDatasourceDetails : undefined,
        location: metaDataSource[crassDataItem.id.toString()]
          ? metaDataSource[crassDataItem.id.toString()].location
          : ({} as ILocationInfo),
        favorite: favoriteMetaData[crassDataItem.id.toString()] ? favoriteMetaData[crassDataItem.id.toString()] : false,
      };
    });
  },
  getSortOptions: () => [SortBy.getRelevancySortSelection(), SortBy.getPopularitySortSelection()],
  shouldBeAvailable: () => true,
  favoritable: () => true,
  getCustomFavoriteID: (item: SearchResultItem) => {
    return (item as DataSource).datasourceId;
  },
};
